"use client";
// 🔄 Stale-build guard.
//
// A deploy renames every JS chunk. A tab that was already open still holds the
// OLD chunk names, so the moment it needs one it has not loaded yet — clicking
// through to Wheel, Gains, anything — the fetch 404s and Next silently gives
// up. To the person at the desk the link simply "does not open", with nothing
// in the UI to explain it and no error to report.
//
// Chunk-load failures are the one class of error where reloading is not a
// guess: the code on disk genuinely moved. Reload once per deploy and the tab
// heals itself. The sessionStorage stamp keeps a genuinely broken chunk from
// turning into a reload loop.
import { useEffect } from "react";

const KEY = "mymtree.stale-reload-at";
const COOLDOWN_MS = 60_000;

function looksLikeStaleChunk(msg: string): boolean {
  return /ChunkLoadError|Loading chunk \d+ failed|Loading CSS chunk|Failed to fetch dynamically imported module|error loading dynamically imported module/i
    .test(msg);
}

function healOnce(reason: string): void {
  try {
    const last = Number(window.sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last < COOLDOWN_MS) return;   // already tried — let it surface
    window.sessionStorage.setItem(KEY, String(Date.now()));
    console.warn(`[stale-build-guard] ${reason} — reloading for the current build`);
    window.location.reload();
  } catch {
    /* storage unavailable — better to do nothing than to loop */
  }
}

export default function StaleBuildGuard() {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      if (looksLikeStaleChunk(e.message || "")) healOnce(e.message);
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      const msg = typeof r === "string" ? r : (r?.message ?? "");
      if (looksLikeStaleChunk(String(msg))) healOnce(String(msg));
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return null;
}
